import React, { useState, useRef } from 'react';
import { ArrowUpTrayIcon, XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface ImageUploaderProps {
    imageUrl?: string;
    onUpload: (file: File) => Promise<void>;
    onRemove?: () => Promise<void>;
    label: string;
    helpText?: string;
    maxSizeMB?: number;
}

/**
 * Компонент для загрузки одной картинки
 */
export const ImageUploader: React.FC<ImageUploaderProps> = ({
    imageUrl,
    onUpload,
    onRemove,
    label,
    helpText = 'Форматы: JPEG, PNG, WEBP',
    maxSizeMB = 50,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            await handleFile(files[0]);
        }
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            await handleFile(files[0]);
        }
        // Сброс значения input чтобы можно было загрузить тот же файл снова
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFile = async (file: File) => {
        setError(null);

        // Проверка типа файла
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Недопустимый формат файла. Разрешены: JPEG, PNG, WEBP');
            return;
        }

        // Проверка размера
        const maxSize = maxSizeMB * 1024 * 1024;
        if (file.size > maxSize) {
            setError(`Размер файла не должен превышать ${maxSizeMB} МБ`);
            return;
        }

        setUploading(true);
        try {
            await onUpload(file);
        } catch (err: any) {
            setError(err.message || 'Ошибка при загрузке файла');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!onRemove) return;
        
        setUploading(true);
        try {
            await onRemove();
        } catch (err: any) {
            setError(err.message || 'Ошибка при удалении файла');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}
            </label>

            {imageUrl ? (
                <div className="relative inline-block">
                    <img
                        src={imageUrl}
                        alt="Uploaded"
                        className="max-w-xs max-h-64 rounded-lg border border-gray-300"
                    />
                    {onRemove && (
                    <button
                        onClick={handleRemove}
                        disabled={uploading}
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 disabled:opacity-50"
                        title="Удалить изображение"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                    )}
                </div>
            ) : (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileInput}
                        className="hidden"
                    />
                    
                    {uploading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600">Загрузка...</span>
                        </div>
                    ) : (
                        <>
                            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                            <p className="text-sm text-gray-600 mb-1">
                                Перетащите изображение или нажмите для выбора
                            </p>
                            <p className="text-xs text-gray-500">{helpText}</p>
                            <p className="text-xs text-gray-500">Максимальный размер: {maxSizeMB} МБ</p>
                        </>
                    )}
                </div>
            )}

            {error && (
                <div className="mt-2 text-sm text-red-600">
                    {error}
                </div>
            )}
        </div>
    );
};

